import { FileText } from 'lucide-react'
import { DataTable, Modal, type DataTableColumn } from '@atrvd/saas-ui'
import { formatYMDtoMDY } from './format-utils'
import { useFileImportProjections, useFileImportProjectionsCount } from '@/hooks/useBanking'
import { useTablePagination } from '@/hooks/useTablePagination'
import { pageOrCountError, queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import type { FileImportProjectionRow } from '@/lib/db-tenant'
import type { FileImportRow } from '@/types/banking'
import { formatCategoryAmount } from '@dcp/shared/utils/formatters'

/**
 * Format a projection's signed amount for display.
 *
 * DCP-250: projections carry a single signed `amount` (positive = inflow,
 * negative = outflow). Positives render green, negatives render red with
 * parentheses (via the shared `formatCategoryAmount` convention). A zero
 * amount is shown in muted style as `$0.00`.
 *
 * @param amount - Signed projection amount (positive = inflow, negative = outflow)
 * @returns Object with formatted `text` and Tailwind `className`
 */
function formatProjectionAmount(amount: number): { text: string; className: string } {
  if (!amount) {
    // Zero (or missing) amount has no flow direction — render muted.
    return { text: '$0.00', className: 'text-muted-foreground' }
  }
  return formatCategoryAmount(amount)
}

const COLUMNS: DataTableColumn<FileImportProjectionRow>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (pj) => formatYMDtoMDY(pj.date),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'category',
    header: 'Category',
    render: (pj) => pj.categories?.name ?? '-',
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'facility',
    header: 'Facility',
    render: (pj) => pj.facilities?.name ?? '-',
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'description',
    header: 'Description',
    render: (pj) => (
      <div className="max-w-[200px] truncate" title={pj.description ?? undefined}>
        {pj.description ?? '-'}
      </div>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    render: (pj) => {
      const amt = formatProjectionAmount(pj.amount)
      return <span className={amt.className}>{amt.text}</span>
    },
    cellClassName: 'whitespace-nowrap font-medium',
  },
  {
    key: 'notes',
    header: 'Notes',
    render: (pj) => (
      <div className="max-w-[150px] truncate" title={pj.notes ?? undefined}>
        {pj.notes ?? '-'}
      </div>
    ),
  },
]

const EMPTY_PROJECTIONS: FileImportProjectionRow[] = []

/**
 * Modal showing paginated projections for a specific file import.
 * Displays date, category, facility, description, amount, and notes.
 *
 * @param fileImport - The file import record to show projections for (null = closed)
 * @param onClose - Callback to close the modal
 */
export function ProjectionImportDetailsModal({
  fileImport,
  onClose,
}: {
  fileImport: FileImportRow | null
  onClose: () => void
}) {
  const {
    data: pjTotal,
    isFetching: countFetching,
    error: countError,
    refetch: refetchCount,
  } = useFileImportProjectionsCount(fileImport?.id ?? null)
  // Another import is another result set: start it on page 1.
  const pagination = useTablePagination({ totalItems: pjTotal, resetKey: fileImport?.id })
  const rowsQuery = useFileImportProjections(
    fileImport?.id ?? null,
    { limit: pagination.pageSize, offset: pagination.offset },
  )
  const rows = rowsQuery.data ?? EMPTY_PROJECTIONS
  const tableState = tableQueryState(
    {
      isPending: rowsQuery.isPending,
      isPlaceholderData: rowsQuery.isPlaceholderData,
      isFetching: rowsQuery.isFetching || countFetching,
      error: pageOrCountError(rowsQuery.error, countError),
      refetch: () => {
        void rowsQuery.refetch()
        void refetchCount()
      },
    },
    rows.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, rows.length)

  return (
    <Modal
      isOpen={!!fileImport}
      onClose={onClose}
      title={`Imported Projections — ${fileImport?.file_name ?? ''}`}
      size="xl"
      className="!max-w-[1100px]"
    >
      <div className="space-y-4">
        {/* No count yet (or the count failed): saying "0" next to real rows would be false. */}
        {pjTotal !== undefined && (
          <p className="text-sm text-muted-foreground">
            {pjTotal} projection{pjTotal !== 1 ? 's' : ''} imported from this file
          </p>
        )}

        {errorDisplay.banner}
        <DataTable
          columns={COLUMNS}
          data={rows}
          rowKey={(pj) => pj.id}
          loading={tableState.loading}
          refreshing={tableState.refreshing}
          // Compact cells: DataTable's default p-4 grows rows to ~53px and
          // widens the six columns past the modal, clipping the last one.
          density="compact"
          // Scroll with the Modal body: a scroller of its own would nest a second
          // scrollbar and capture the sticky header.
          scroll="parent"
          stickyHeader
          error={errorDisplay.tableError}
          emptyState={
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No projections found</p>
            </div>
          }
          itemName="projections"
          pagination={pagination.dataTablePagination}
        />
      </div>
    </Modal>
  )
}
